/**
 * ============================================================================
 * EXPERIMENT: Calendly Integration (ab_calendly)
 * PR: #TODO_AFTER_PR_CREATION_ab_calendly
 *
 * Page displaying upcoming Calendly scheduled events.
 * Temporary code — will be REMOVED when the experiment concludes.
 *
 * Find all experiment code: grep -r "ab_calendly" src/
 * ============================================================================
 */

import { Box, Chip, Paper, Stack, Typography, paletteColors } from '@evaneos/b2b-ui';
import { format } from 'date-fns';

import AccessTimeIcon from '@evaneos/b2b-ui/icons/AccessTime';
import CalendarMonthIcon from '@evaneos/b2b-ui/icons/CalendarMonth';
import LinkIcon from '@evaneos/b2b-ui/icons/Link';
import PersonIcon from '@evaneos/b2b-ui/icons/PersonOutlined';
import VideocamIcon from '@evaneos/b2b-ui/icons/VideocamOutlined';

import type { ScheduledEvent } from '../../resources/calendly/AB_calendly-types';
import { useCalendlyEvents, useCalendlyUser } from '../../resources/calendly/AB_calendly-queries';

function formatEventTime(startTime: string, endTime: string): string {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return `${format(start, 'dd/MM/yyyy')} · ${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`;
}

function getLocationLabel(event: ScheduledEvent): string | null {
    if (!event.location) return null;
    if (event.location.join_url) return event.location.join_url;
    if (event.location.location) return event.location.location;
    return event.location.type;
}

function EventCard({ event }: { event: ScheduledEvent }): JSX.Element {
    const locationLabel = getLocationLabel(event);
    const hasJoinUrl = !!event.location?.join_url;

    return (
        <Paper
            elevation={0}
            sx={{
                padding: 2.5,
                borderRadius: 2,
                border: `1px solid ${paletteColors.neutral[200]!}`,
            }}
        >
            <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="h6" fontWeight={600}>
                        {event.name}
                    </Typography>
                    <Chip
                        label={`${event.invitees_counter.active} invité(s)`}
                        size="small"
                        icon={<PersonIcon />}
                        sx={{
                            backgroundColor: paletteColors.green[50],
                            color: paletteColors.green[800],
                            '& .MuiChip-icon': { color: paletteColors.green[800] },
                        }}
                    />
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                    <AccessTimeIcon fontSize="small" sx={{ color: paletteColors.neutral[500] }} />
                    <Typography variant="body2" color="text.secondary">
                        {formatEventTime(event.start_time, event.end_time)}
                    </Typography>
                </Stack>

                {locationLabel && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {hasJoinUrl ? (
                            <VideocamIcon
                                fontSize="small"
                                sx={{ color: paletteColors.neutral[500] }}
                            />
                        ) : (
                            <LinkIcon fontSize="small" sx={{ color: paletteColors.neutral[500] }} />
                        )}
                        {hasJoinUrl ? (
                            <Typography
                                variant="body2"
                                component="a"
                                href={event.location!.join_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: paletteColors.green[700], textDecoration: 'none' }}
                            >
                                Rejoindre la visio
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {locationLabel}
                            </Typography>
                        )}
                    </Stack>
                )}

                {event.event_memberships.length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {event.event_memberships.map((member) => (
                            <Chip
                                key={member.user}
                                label={member.user_name || member.user_email}
                                size="small"
                                variant="outlined"
                            />
                        ))}
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
}

export function AB_CalendlyEvents(): JSX.Element {
    const { data: user, isLoading: isLoadingUser } = useCalendlyUser();
    const { data: eventsResponse, isLoading: isLoadingEvents } = useCalendlyEvents(user?.uri);

    const isLoading = isLoadingUser || isLoadingEvents;
    const events = eventsResponse?.collection ?? [];

    return (
        <Box
            sx={{
                maxWidth: 800,
                margin: '0 auto',
                padding: 3,
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    padding: 3,
                    borderRadius: 2,
                    backgroundColor: paletteColors.neutral[0],
                    border: `1px solid ${paletteColors.neutral[200]!}`,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ marginBottom: 3 }}>
                    <CalendarMonthIcon sx={{ color: paletteColors.green[600] }} />
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                        Mes prochains rendez-vous
                    </Typography>
                </Stack>

                {isLoading && (
                    <Stack spacing={2}>
                        {[1, 2, 3].map((i) => (
                            <Box
                                key={i}
                                sx={{
                                    padding: 2.5,
                                    borderRadius: 2,
                                    border: `1px solid ${paletteColors.neutral[200]!}`,
                                    height: 120,
                                    animation: 'pulse 1.5s ease-in-out infinite',
                                    '@keyframes pulse': {
                                        '0%, 100%': { opacity: 1 },
                                        '50%': { opacity: 0.4 },
                                    },
                                }}
                            />
                        ))}
                    </Stack>
                )}

                {!isLoading && events.length === 0 && (
                    <Box sx={{ padding: 4, textAlign: 'center' }}>
                        <CalendarMonthIcon
                            sx={{
                                fontSize: 48,
                                color: paletteColors.neutral[300],
                                marginBottom: 1,
                            }}
                        />
                        <Typography variant="body1" color="text.secondary">
                            Aucun rendez-vous à venir
                        </Typography>
                    </Box>
                )}

                {!isLoading && events.length > 0 && (
                    <Stack spacing={2}>
                        {events.map((event) => (
                            <EventCard key={event.uri} event={event} />
                        ))}
                    </Stack>
                )}
            </Paper>
        </Box>
    );
}
